/** Client-side prefs + filtering for FlowV2 editor message banners (server Message / LogItem / errors). */

export const MESSAGE_LOG_SETTING_KEY = 'messageLogPrefs';
export const MESSAGE_LOG_MAX = 400;

export function defaultMessageLogPrefs() {
    return {
        showInfo: true,
        showWarning: true,
        showError: true,
        textFilter: '',
        mutePatterns: [],
        collapsed: false,
    };
}

export function normalizeMessageLogPrefs(raw) {
    const base = defaultMessageLogPrefs();
    if (!raw || typeof raw !== 'object')
        return base;
    const mutePatterns = Array.isArray(raw.mutePatterns)
        ? raw.mutePatterns.map(p => String(p ?? '').trim()).filter(Boolean)
        : [];
    return {
        showInfo: raw.showInfo !== false,
        showWarning: raw.showWarning !== false,
        showError: raw.showError !== false,
        textFilter: typeof raw.textFilter === 'string' ? raw.textFilter : '',
        mutePatterns,
        collapsed: !!raw.collapsed,
    };
}

export function messageColorKey(color) {
    const c = String(color || 'info').toLowerCase();
    if (c === 'error' || c === 'negative')
        return 'error';
    if (c === 'warning' || c === 'warn')
        return 'warning';
    return 'info';
}

function patternMatch(text, pattern) {
    if (!pattern)
        return false;
    return String(text || '').toLowerCase().includes(String(pattern).toLowerCase());
}

/** True when any mute pattern matches (suppress at ingest). */
export function isMutedByPatterns(text, patterns) {
    if (!patterns || !patterns.length)
        return false;
    const hay = String(text || '');
    return patterns.some(p => patternMatch(hay, p));
}

/** Display filter: severity toggles + optional include substring. */
export function matchesMessageFilters(msg, prefs) {
    const p = prefs || defaultMessageLogPrefs();
    const key = messageColorKey(msg?.color);
    if (key === 'error' && !p.showError)
        return false;
    if (key === 'warning' && !p.showWarning)
        return false;
    if (key === 'info' && !p.showInfo)
        return false;
    const needle = String(p.textFilter || '').trim();
    if (needle && !patternMatch(msg?.text, needle))
        return false;
    return true;
}

export function filterVisibleMessages(messages, prefs) {
    const list = Array.isArray(messages) ? messages : [];
    return list.filter(m => matchesMessageFilters(m, prefs));
}

let _msgSeq = 0;

export function makeFlowMessage(text, color = 'info') {
    return {
        id: `fm-${Date.now()}-${++_msgSeq}`,
        text: String(text ?? ''),
        color: messageColorKey(color),
        at: Date.now(),
    };
}

/**
 * Append a message unless muted. Caps list length (drops oldest).
 * @returns {{ messages: object[], appended: boolean, muted: boolean }}
 */
export function appendFlowMessage(messages, { text, color } = {}, prefs, { max = MESSAGE_LOG_MAX } = {}) {
    const list = Array.isArray(messages) ? messages.slice() : [];
    const p = prefs || defaultMessageLogPrefs();
    if (isMutedByPatterns(text, p.mutePatterns))
        return { messages: list, appended: false, muted: true };

    list.push(makeFlowMessage(text, color));
    if (list.length > max)
        list.splice(0, list.length - max);
    return { messages: list, appended: true, muted: false };
}

export function addMutePattern(prefs, pattern) {
    const p = normalizeMessageLogPrefs(prefs);
    const trimmed = String(pattern ?? '').trim();
    if (!trimmed)
        return p;
    if (p.mutePatterns.some(x => x.toLowerCase() === trimmed.toLowerCase()))
        return p;
    return { ...p, mutePatterns: [...p.mutePatterns, trimmed] };
}

export function removeMutePattern(prefs, pattern) {
    const p = normalizeMessageLogPrefs(prefs);
    const target = String(pattern ?? '').toLowerCase();
    return {
        ...p,
        mutePatterns: p.mutePatterns.filter(x => x.toLowerCase() !== target),
    };
}

/** Prefer a short mute substring when silencing a full log line from the UI. */
export function suggestMutePattern(text, { maxLen = 48 } = {}) {
    const raw = String(text ?? '').replace(/\s+/g, ' ').trim();
    if (!raw)
        return '';
    // Platform.VoiceExt.CaptureDTMF(...) → CaptureDTMF
    const call = raw.match(/(?:^|[.\s])([A-Za-z_][A-Za-z0-9_]*)\s*\(/);
    if (call?.[1] && call[1].length >= 4 && call[1].length <= 40)
        return call[1];
    // Leading JSON key: {"Success":true... → "Success"
    const jsonKey = raw.match(/^\{"([^"]{3,40})"/);
    if (jsonKey?.[1])
        return `"${jsonKey[1]}"`;
    if (raw.length <= maxLen)
        return raw;
    return raw.slice(0, maxLen).trim();
}

/** Truncate for list display; full pattern stays in title/tooltip. */
export function formatMutePatternLabel(pattern, { maxLen = 56 } = {}) {
    const raw = String(pattern ?? '');
    if (raw.length <= maxLen)
        return raw;
    return `${raw.slice(0, Math.max(0, maxLen - 1)).trimEnd()}…`;
}
